import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, X, CalendarCheck, Loader2, Plane, Stethoscope, User2, FileText } from "lucide-react";

const TYPE_META = {
  vacation: { label: "Vacaciones", icon: Plane, color: "text-sky-600 bg-sky-50" },
  sick: { label: "Baja médica", icon: Stethoscope, color: "text-rose-600 bg-rose-50" },
  personal: { label: "Asuntos propios", icon: User2, color: "text-violet-600 bg-violet-50" },
  other: { label: "Otros", icon: FileText, color: "text-slate-600 bg-slate-100" },
};
const STATUS = {
  pending: { label: "Pendiente", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Aprobada", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rechazada", cls: "bg-rose-100 text-rose-700" },
};
const FILTERS = [{ k: "pending", l: "Pendientes" }, { k: "approved", l: "Aprobadas" }, { k: "rejected", l: "Rechazadas" }, { k: "", l: "Todas" }];

export default function Approvals() {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get(`/absences${filter ? `?status=${filter}` : ""}`);
    setRows(data); setLoading(false);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const review = async (id, status) => {
    try {
      await api.patch(`/absences/${id}`, { status });
      toast.success(status === "approved" ? "Solicitud aprobada" : "Solicitud rechazada");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fmt = (d) => new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Aprobaciones</h1>
        <p className="text-slate-500 text-sm mt-1">Gestiona las solicitudes de vacaciones y ausencias.</p>
      </div>

      <div className="flex bg-white border border-slate-200 rounded-lg p-1 w-fit">
        {FILTERS.map((f) => (
          <button key={f.k} data-testid={`filter-${f.k || "all"}`} onClick={() => setFilter(f.k)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${filter === f.k ? "text-white" : "text-slate-500 hover:text-slate-800"}`}
            style={filter === f.k ? { background: "var(--brand)" } : {}}>{f.l}</button>
        ))}
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
      ) : !rows.length ? (
        <Card className="p-12 text-center border-slate-200 shadow-none">
          <CalendarCheck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No hay solicitudes {filter ? STATUS[filter]?.label.toLowerCase() : ""}.</p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {rows.map((r) => {
            const meta = TYPE_META[r.type] || TYPE_META.other;
            return (
              <Card key={r.id} data-testid={`absence-${r.id}`} className="p-4 border-slate-200 shadow-none flex flex-wrap items-center gap-4 hover:shadow-sm transition-all">
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${meta.color}`}><meta.icon className="h-5 w-5" /></div>
                <div className="flex-1 min-w-[180px]">
                  <div className="font-semibold text-slate-800">{r.employee_name}</div>
                  <div className="text-sm text-slate-500">{meta.label} · {fmt(r.start_date)} → {fmt(r.end_date)}</div>
                  {r.reason && <div className="text-xs text-slate-400 mt-0.5">{r.reason}</div>}
                </div>
                <Badge className={`${STATUS[r.status].cls} border-0`}>{STATUS[r.status].label}</Badge>
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => review(r.id, "approved")} data-testid={`approve-${r.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="h-4 w-4 mr-1" /> Aprobar</Button>
                    <Button size="sm" variant="outline" onClick={() => review(r.id, "rejected")} data-testid={`reject-${r.id}`} className="text-rose-600 border-rose-200 hover:bg-rose-50"><X className="h-4 w-4 mr-1" /> Rechazar</Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
